import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STATUS = {
  pending: { Icon: Clock, cls: "bg-amber-50 border-amber-200 text-amber-900", title: "Request pending review", body: "An existing administrator must approve your request. Signing in again does not grant access." },
  approved: { Icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-900", title: "Request approved", body: "Your account has admin access. Open the admin console to continue." },
  rejected: { Icon: XCircle, cls: "bg-rose-50 border-rose-200 text-rose-900", title: "Request rejected", body: "You may submit a new request if your circumstances change." },
};

function StatusCard({ req }) {
  const s = STATUS[req.status];
  return (
    <div className={`rounded-2xl border p-5 ${s.cls}`} data-testid="admin-access-status" data-status={req.status}>
      <div className="flex items-center gap-2 font-bold"><s.Icon size={18} /><span data-testid="admin-access-status-title">{s.title}</span></div>
      <p className="mt-2 text-sm">{s.body}</p>
      {req.decision_reason && req.status === "rejected" && <p className="mt-2 text-sm"><span className="font-bold">Reason:</span> <span data-testid="admin-access-decision-reason">{req.decision_reason}</span></p>}
      <p className="mt-3 text-[11px] opacity-70">Request {req.request_id} · submitted {new Date(req.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</p>
    </div>
  );
}

export default function AdminAccessRequest() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!user && !loading) navigate("/login?next=/admin-access"); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin-access/requests/me").then((r) => setState(r.data)).catch(() => setState({ request: null, role: user.role }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 10) return toast.error("Please describe why you need admin access (at least 10 characters).");
    setBusy(true);
    try {
      const r = await api.post("/admin-access/requests", { reason: reason.trim() });
      setState((s) => ({ ...(s || {}), request: r.data }));
      toast.success("Request submitted — an administrator will review it.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && d.message) || d || "Could not submit request");
    }
    setBusy(false);
  };

  if (loading || !user) return null;
  const req = state?.request;
  const canRequest = user.role === "user" && (!req || req.status === "rejected");

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-10" data-testid="admin-access-page">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">Advoset staff</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Request admin access</h1>
        <p className="mt-3 text-sm text-slate-600">Admin access is never granted automatically. A request is reviewed and must be approved by an existing administrator — someone other than you — with two-factor verification.</p>

        <div className="mt-8 space-y-6">
          {user.role === "admin" && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900" data-testid="admin-access-already-admin">
              <p className="font-bold flex items-center gap-2"><ShieldCheck size={18} /> You already have admin access.</p>
              <Link to="/secure-console" className="mt-3 inline-block text-sm font-bold underline" data-testid="admin-access-open-console">Open the admin console →</Link>
            </div>
          )}
          {user.role === "lawyer" && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-700 text-sm" data-testid="admin-access-lawyer-blocked">
              Admin access requests are not available for lawyer accounts. Advoset keeps lawyer and administrator identities strictly separate.
            </div>
          )}
          {req && <StatusCard req={req} />}
          {canRequest && (
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-access-form">
              <label htmlFor="reason" className="text-xs font-bold uppercase tracking-widest text-slate-500">Why do you need admin access?</label>
              <Textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="e.g. I have joined Advoset operations and need to manage weekly lawyer settlements." className="mt-2" data-testid="admin-access-reason" />
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-[11px] text-slate-400">Signed in as {user.email}</p>
                <Button type="submit" disabled={busy} className="rounded-full bg-[#0A2540] hover:bg-[#123560] font-bold" data-testid="admin-access-submit">{busy ? "Submitting…" : req ? "Submit a new request" : "Submit request"}</Button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
